// /register?token=… — an invitee sets their password to activate their account. After this they
// sign in with email + password + a one-time code (two-factor).
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AuthLayout from '../components/AuthLayout.jsx';
import { validateInvite, registerAccount } from '../api/auth.js';

export default function Register() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [invite, setInvite] = useState(null); // { email, role, alreadyRegistered } | null
  const [state, setState] = useState('loading'); // loading | ready | invalid
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setState('invalid');
      return;
    }
    validateInvite(token)
      .then((d) => {
        setInvite(d);
        setState('ready');
      })
      .catch(() => setState('invalid'));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) return setError('Password must be at least 8 characters.');
    if (password !== confirm) return setError('Passwords do not match.');
    setBusy(true);
    try {
      await registerAccount(token, password);
      navigate('/login', { replace: true, state: { email: invite?.email, registered: true } });
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Could not complete registration.');
    } finally {
      setBusy(false);
    }
  };

  if (state === 'loading') {
    return (
      <AuthLayout>
        <p className="text-sm text-text-muted">Checking your invitation…</p>
      </AuthLayout>
    );
  }

  if (state === 'invalid') {
    // This screen serves BOTH the registration invite and the forgot-password reset. Reset links last
    // ~60 minutes vs 7 days for an invite, so "expired" is overwhelmingly a reset — and telling that
    // user to "ask your TEAMM8 contact" turns a self-service flow into a support email.
    const isReset = params.get('mode') === 'reset';
    return (
      <AuthLayout>
        <h1 className="text-lg font-semibold text-text-primary">
          {isReset ? 'This reset link has expired' : 'This link is no longer valid'}
        </h1>
        <p className="mt-1 text-sm text-text-secondary">
          {isReset
            ? 'Password reset links are only valid for a short time. Request a new one and we\'ll email you a fresh link.'
            : 'This link is invalid or has expired. If you were resetting your password you can request a new link below; if you were activating a new account, ask your TEAMM8 contact to re-send the invitation.'}
        </p>
        <button
          onClick={() => navigate('/login', { state: { forgot: true } })}
          className="mt-5 w-full rounded-md bg-accent px-4 py-2.5 text-sm font-medium text-white hover:bg-accent-hover"
        >
          Email me a new reset link
        </button>
        <button
          onClick={() => navigate('/login')}
          className="mt-2 w-full rounded-md border border-line px-4 py-2.5 text-sm font-medium text-text-secondary hover:bg-surface-raised"
        >
          Go to sign in
        </button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <h1 className="text-lg font-semibold text-text-primary">Set your password</h1>
      <p className="mt-1 text-sm text-text-secondary">
        You're setting the password for{' '}
        <span className="font-medium text-text-primary">{invite.email}</span>. After this, each sign-in also sends a
        one-time code to your email.
      </p>
      {invite.alreadyRegistered && (
        <p className="mt-3 rounded-md bg-warning-light px-3 py-2 text-xs text-warning">
          This account already has a password — setting a new one will replace it.
        </p>
      )}

      <form onSubmit={submit} className="mt-5 space-y-4">
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-text-primary">
            Password
          </label>
          <input
            id="password"
            type="password"
            name="new-password"
            autoComplete="new-password"
            required
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 8 characters"
            className="mt-1.5 w-full rounded-md border border-line bg-surface px-3 py-2.5 text-sm text-text-primary outline-none transition placeholder:text-text-muted focus:border-accent focus:ring-2 focus:ring-accent/30"
          />
        </div>
        <div>
          <label htmlFor="confirm" className="block text-sm font-medium text-text-primary">
            Confirm password
          </label>
          <input
            id="confirm"
            type="password"
            name="confirm-password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Re-enter your password"
            className="mt-1.5 w-full rounded-md border border-line bg-surface px-3 py-2.5 text-sm text-text-primary outline-none transition placeholder:text-text-muted focus:border-accent focus:ring-2 focus:ring-accent/30"
          />
        </div>
        {error && <p className="text-sm text-error">{error}</p>}
        <button
          type="submit"
          disabled={busy || !password || !confirm}
          className="w-full rounded-md bg-accent px-4 py-2.5 text-sm font-medium text-white transition hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy ? 'Setting password…' : 'Activate account'}
        </button>
      </form>
    </AuthLayout>
  );
}
